import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { map, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import type { EmployeePermission } from '../../shared/constants/permissions';
import { AuthService } from '../../shared/services/auth.service';
import { SessionStore } from '../../shared/stores/session.store';
import { ROUTE_LOGIN } from '../../shared/constants/routes';
import { Session } from '../../shared/models/session';

/**
 * Protects routes that require a specific employee permission.
 * Waits for session load when still resolving auth state.
 */
export const permissionGuard = (required: EmployeePermission): CanActivateFn => {
  return () => {
    const sessionStore = inject(SessionStore);
    const auth = inject(AuthService);
    const router = inject(Router);
    const toastr = inject(ToastrService);

    const check = (session: Session) => {
      // If session isn't found, return login
      if (session === null) return router.createUrlTree([ROUTE_LOGIN]);

      // If session has required permission, return true
      if (session.hasPermission(required)) return true;

      // Route back to home
      toastr.error('You do not have permission to access this page.');
      return router.createUrlTree(['/']);
    };

    // Check current nav attempt
    const session = sessionStore.currentSession();
    if (session != undefined) return check(session);

    // If session hasn't loaded, wait for session to load and then check
    return auth.loadSession().pipe(
      take(1),
      map((session: Session | null) => {
        if (session === null) return router.createUrlTree(['/']);

        return check(session);
      }),
    );
  };
};
